import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "aa_quote_popup_dismissed_at";
const DISMISS_HOURS = 24;
const SHOW_DELAY_MS = 30000; // 30 seconds

const QuotePopup = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", travel_date: "", package_interest: "" });
  const [submitting, setSubmitting] = useState(false);
  const [packages, setPackages] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Do not show on admin, login, register, reset flows, or if recently dismissed
    const path = window.location.pathname;
    const excluded = ["/admin", "/login", "/register", "/forgot-password", "/reset-password",
                      "/auth/callback", "/payment-success", "/payment-cancel"];
    if (excluded.some((p) => path.startsWith(p))) return;

    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const hoursAgo = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60);
      if (hoursAgo < DISMISS_HOURS) return;
    }

    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (open && packages.length === 0) {
      publicApi.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    }
  }, [open, packages.length]);

  const close = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post("/inquiries/quote", form);
      setDone(true);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      toast.success("Got it! We'll call you within 2 hours.");
    } catch (err) {
      toast.error("Failed to send. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={close}
          data-testid="quote-popup-backdrop"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            data-testid="quote-popup"
          >
            <button
              onClick={close}
              data-testid="quote-popup-close"
              className="absolute top-3 left-3 z-10 rounded-full p-1.5 bg-black/5 hover:bg-black/10 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {done ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/20 flex items-center justify-center">
                  <Sparkles className="text-secondary" size={28} />
                </div>
                <h3 className="text-2xl font-serif text-primary mb-2">You're all set!</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  Our travel expert will call you within <strong>2 hours</strong> with a personalised itinerary and best price.
                </p>
                <Button data-testid="quote-popup-ok" onClick={close} className="bg-primary hover:bg-primary/90">Continue browsing</Button>
              </div>
            ) : (
              <>
                <div className="bg-primary text-primary-foreground p-6 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="text-secondary" size={22} />
                    <span className="accent-label text-secondary">FREE QUOTE</span>
                  </div>
                  <h3 className="text-2xl font-serif leading-tight">Get a personalised Andaman itinerary</h3>
                  <p className="text-primary-foreground/80 text-sm mt-2">
                    Leave your details — our expert will call within 2 hours with the best price.
                  </p>
                </div>

                <form onSubmit={handleSubmit} data-testid="quote-popup-form" className="p-6 space-y-3">
                  <Input
                    data-testid="quote-name"
                    placeholder="Your name *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                  <Input
                    data-testid="quote-phone"
                    type="tel"
                    placeholder="Phone / WhatsApp *"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required
                  />
                  <select
                    data-testid="quote-package"
                    value={form.package_interest}
                    onChange={(e) => setForm({ ...form, package_interest: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Interested in… (optional)</option>
                    {packages.map((p) => {
                      const priceText = p.price ? p.price.toLocaleString('en-IN') : '';
                      const label = priceText ? `${p.name} - INR ${priceText}` : p.name;
                      return <option key={p.id} value={p.name}>{label}</option>;
                    })}
                    <option value="Custom itinerary">Custom itinerary</option>
                  </select>
                  <Input
                    data-testid="quote-travel-date"
                    type="date"
                    value={form.travel_date}
                    onChange={(e) => setForm({ ...form, travel_date: e.target.value })}
                    min={new Date().toISOString().split("T")[0]}
                    placeholder="Preferred travel date (optional)"
                  />

                  <Button
                    type="submit"
                    data-testid="quote-submit-btn"
                    disabled={submitting}
                    className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground h-11"
                  >
                    {submitting ? "Sending..." : "Get My Free Quote"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center pt-1">
                    No spam. We only use your details to send you the quote.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuotePopup;
